import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, Shield, Clock, Users, ArrowRight, Car, FileText, Stethoscope, Scale, AlertTriangle, AlertOctagon, ExternalLink } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const DrunkDriverAccidentPage = () => {
  const faqData = [
    {
      question: "Can I sue a drunk driver who hit me?",
      answer: "Yes. You can file a civil lawsuit against a drunk driver to recover compensation for your injuries, medical bills, lost wages, and pain and suffering. This is separate from any criminal charges they may face."
    },
    {
      question: "What if the drunk driver doesn't have insurance or money?",
      answer: "You may still have options. Your own uninsured/underinsured motorist coverage can provide compensation. I also investigate whether other parties (like bars that over-served the driver) may be liable."
    },
    {
      question: "How does the criminal case affect my civil claim?",
      answer: "A criminal conviction can help your civil case by establishing the driver's wrongdoing. However, you don't have to wait for criminal proceedings—I can pursue your claim immediately."
    },
    {
      question: "Can I get punitive damages from a drunk driver?",
      answer: "In Washington, you may be entitled to punitive damages if the driver's conduct was particularly reckless or egregious. Drunk driving can meet this standard. I'll evaluate whether your case qualifies."
    },
    {
      question: "How much is a drunk driving accident case worth?",
      answer: "Drunk driving cases often result in higher compensation because of the driver's clear negligence. Value depends on injury severity, medical costs, lost wages, and pain and suffering. I'll give you an honest assessment."
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: "Free Consultation",
      description: "Call or text me directly. Tell me what happened and I'll explain your options for holding the drunk driver accountable.",
      icon: Phone
    },
    {
      step: 2,
      title: "I Take Over",
      description: "I handle all communication with insurance companies and coordinate with any criminal proceedings if applicable.",
      icon: Shield
    },
    {
      step: 3,
      title: "You Focus on Healing",
      description: "Your only job is to recover. I'll handle the legal battle against the drunk driver and their insurance company.",
      icon: Stethoscope
    },
    {
      step: 4,
      title: "I Build Your Case",
      description: "I gather evidence including police reports, BAC results, witness statements, and documentation of your injuries and losses.",
      icon: FileText
    },
    {
      step: 5,
      title: "Maximum Compensation",
      description: "I fight for every dollar you deserve—including potential punitive damages. You decide whether to settle or go to trial.",
      icon: Scale
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="drunk-driver-accident-page">
      <SEOHead 
        title="Drunk Driver Accident Lawyer | DUI Crash Injury Claims | Amy C. Brown Law"
        description="Injured by a drunk driver? I fight for maximum compensation including potential punitive damages. Former insurance defense attorney. No fees unless you win. Call (425) 675-1224."
        faqData={faqData}
        serviceData={{
          name: "Drunk Driving Accident Legal Representation",
          description: "Legal representation for victims of drunk driving accidents in Washington State. Specializing in DUI crash injuries, punitive damages, dram shop liability, and maximum compensation recovery.",
          category: "Personal Injury Law - DUI/Drunk Driving Accidents"
        }}
      />

      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <AlertOctagon className="w-4 h-4" />
              Drunk Driving Accident Attorney
            </div>
            
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6 leading-tight">
              Hit by a Drunk Driver?
            </h1>
            
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Someone chose to get behind the wheel drunk, and now you're paying the price. The injuries, the medical bills, the anger of knowing this never should have happened—I understand what you're going through.
            </p>

            <div className="flex flex-wrap gap-4">
              <a 
                href="tel:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold hover:bg-primary/90 transition-all"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a 
                href="sms:425-675-1224"
                className="inline-flex items-center justify-center gap-2 border-2 border-secondary text-secondary px-8 py-4 rounded-full font-semibold hover:bg-secondary hover:text-white transition-all"
                data-testid="hero-text-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* The Reality Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              This Should Never Have Happened
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Someone made a terrible choice, and now you're dealing with the consequences. I've guided many people through exactly what you're facing.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: AlertTriangle, title: "The Injuries Are Serious", desc: "Drunk driving crashes often cause severe injuries—broken bones, head trauma, back injuries. Recovery takes time." },
              { icon: FileText, title: "Bills Keep Coming", desc: "Hospital, doctors, rehabilitation—the costs pile up while you're trying to heal and can't work." },
              { icon: Clock, title: "Insurance Still Plays Games", desc: "Even when the other driver was clearly at fault, their insurance company will still try to minimize what they pay." },
              { icon: AlertOctagon, title: "The Anger Is Real", desc: "This was 100% preventable. Someone chose to drink and drive. You have every right to be angry." }
            ].map((item, index) => (
              <div key={index} className="bg-slate-50 p-6 rounded-xl border border-slate-100">
                <item.icon className="w-10 h-10 text-red-600 mb-4" />
                <h3 className="font-heading font-semibold text-secondary mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-accent-soft rounded-2xl p-8 border-l-4 border-red-500 max-w-3xl mx-auto">
            <p className="text-slate-700 leading-relaxed">
              I've been a lawyer for over ten years and have guided countless people through the chaos you may be going through. <strong>I'll listen, explain your options clearly, and handle the complexities so you can focus on healing.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* What Makes DUI Cases Different */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-8 text-center">
              What Makes Drunk Driving Accident Cases Different
            </h2>
            
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="font-heading font-semibold text-secondary mb-2">Criminal vs. Civil Cases</h3>
                <p className="text-slate-600">The drunk driver may face criminal charges (DUI), but that's separate from your civil claim for compensation. Criminal cases punish the driver; civil cases compensate you for your losses. I handle the civil side—getting you the money you deserve.</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="font-heading font-semibold text-secondary mb-2">Stronger Liability = Stronger Case</h3>
                <p className="text-slate-600">When a driver is drunk, liability is usually clear. BAC test results, police reports, and criminal proceedings can all support your civil claim. I know how to leverage this evidence for maximum compensation.</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="font-heading font-semibold text-secondary mb-2">Potential for Punitive Damages</h3>
                <p className="text-slate-600">Washington allows punitive damages in cases involving gross negligence or reckless conduct. Choosing to drive drunk can qualify. I'll evaluate whether your case may be eligible for additional damages beyond compensation for your injuries.</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="font-heading font-semibold text-secondary mb-2">Multiple Sources of Compensation</h3>
                <p className="text-slate-600">Beyond the drunk driver's insurance, you may have claims against bars or restaurants that over-served the driver (dram shop liability), your own UIM coverage, or other parties. I investigate every possible source of recovery.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              How Your Drunk Driving Accident Case Works
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Clear steps, constant communication. You'll always know what's happening.
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {processSteps.map((step) => (
              <div key={step.step} className="relative">
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-100 h-full">
                  <div className="bg-primary text-white w-8 h-8 rounded-full flex items-center justify-center font-bold mb-4">
                    {step.step}
                  </div>
                  <step.icon className="w-8 h-8 text-primary mb-3" />
                  <h3 className="font-heading font-semibold text-secondary mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm">{step.description}</p>
                </div>
                {step.step < 5 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-slate-300">
                    <ArrowRight className="w-6 h-6" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl font-bold text-secondary mb-6">
                Why Hire Me for Your Drunk Driving Accident Case
              </h2>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="bg-primary/10 p-3 rounded-full h-fit">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-1">Former Insurance Defense Attorney</h3>
                    <p className="text-slate-600">I know the tactics insurance companies use to minimize claims—even in clear-cut drunk driving cases. I use that knowledge to fight for you.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-primary/10 p-3 rounded-full h-fit">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-1">You Work Directly With Me</h3>
                    <p className="text-slate-600">No hand-offs to paralegals. Your case is too important. When you call, you reach your attorney.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-primary/10 p-3 rounded-full h-fit">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-1">I Understand the Trauma</h3>
                    <p className="text-slate-600">I've experienced family tragedy myself. I understand the pain and anger you're feeling. I'll fight for the justice you deserve.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="bg-primary/10 p-3 rounded-full h-fit">
                    <Scale className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-secondary mb-1">Maximum Compensation</h3>
                    <p className="text-slate-600">I pursue every avenue of recovery—drunk driver, their insurance, dram shop claims, your own coverage, and potential punitive damages.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="font-heading text-2xl font-bold text-secondary mb-6">
                Free Case Review
              </h3>
              <p className="text-slate-600 mb-6">
                You shouldn't have to navigate this alone. Call me and I'll explain your options for holding the drunk driver accountable and getting the compensation you need.
              </p>
              <div className="space-y-4">
                <a 
                  href="tel:425-675-1224"
                  className="flex items-center justify-center gap-2 bg-primary text-white px-6 py-4 rounded-full font-semibold hover:bg-primary/90 transition-all w-full"
                >
                  <Phone className="w-5 h-5" />
                  Call (425) 675-1224
                </a>
                <a 
                  href="sms:425-675-1224"
                  className="flex items-center justify-center gap-2 border-2 border-secondary text-secondary px-6 py-4 rounded-full font-semibold hover:bg-secondary hover:text-white transition-all w-full"
                >
                  <MessageCircle className="w-5 h-5" />
                  Text Me Now
                </a>
              </div>
              <p className="text-sm text-slate-500 mt-4 text-center">
                I respond personally within 1 business day
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-secondary mb-4">
              Common Questions About Drunk Driving Accident Claims
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-slate-50 p-6 rounded-xl">
                <h3 className="font-heading font-semibold text-secondary mb-3">{faq.question}</h3>
                <p className="text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Concepts Section - Entity SEO */}
      <section className="py-12 bg-slate-50 border-t border-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <h2 className="font-heading text-xl font-semibold text-secondary mb-6 text-center">
            Understanding Drunk Driving Accident Law
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a href="https://en.wikipedia.org/wiki/Driving_under_the_influence" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                DUI/DWI <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Punitive_damages" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Punitive Damages <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Dram_shop" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Dram Shop Liability <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a href="https://en.wikipedia.org/wiki/Gross_negligence" target="_blank" rel="noopener noreferrer" className="bg-white p-4 rounded-lg hover:bg-primary/5 transition-colors text-center group">
              <span className="text-sm text-slate-700 group-hover:text-primary font-medium inline-flex items-center gap-1">
                Gross Negligence <ExternalLink className="w-3 h-3" />
              </span>
            </a>
          </div>
          <p className="text-xs text-slate-400 text-center mt-4">
            Learn more about these legal concepts on Wikipedia
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-6">
            They Made a Terrible Choice. Make Them Pay.
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            You focus on healing. I'll fight for the justice and compensation you deserve.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-primary/90 transition-all"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DrunkDriverAccidentPage;
